<template>
  <div>
    <component
      :is="item.component"
      v-for="(item, index) in components"
      :key="item.props.__typename + '-' + index"
      v-bind="item.props"
    />
  </div>
</template>

<script setup lang="ts">
import { useStrapiUtils } from "~/composables/useStrapiUtils";
import { computed } from "vue";
import type { PropType } from "vue";

// Helper function to sanitize objects in props to prevent [object Object] URL issues
function sanitizeProps(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeProps(item));
  }

  const result: any = {};

  for (const [key, value] of Object.entries(obj)) {
    // Detect properties that might be used in URLs
    const isLikelyUrlProp =
      key === "url" ||
      key === "link" ||
      key === "href" ||
      key === "to" ||
      key === "path" ||
      key.includes("Url") ||
      key.includes("Link");

    if (isLikelyUrlProp && typeof value === "object" && value !== null) {
      // For URL-related properties, convert objects to strings
      const objValue = value as Record<string, any>;
      if (objValue.hasOwnProperty("url")) {
        result[key] = objValue.url;
      } else if (objValue.hasOwnProperty("link")) {
        result[key] = objValue.link;
      } else if (objValue.hasOwnProperty("path")) {
        result[key] = objValue.path;
      } else if (objValue.hasOwnProperty("href")) {
        result[key] = objValue.href;
      } else {
        // If we can't safely extract a URL, use empty string
        console.warn(
          `[DynamicZone] Potentially unsafe URL object detected in prop '${key}':`,
          value
        );
        result[key] = "";
      }
    } else {
      // For all other properties, recursively sanitize
      result[key] = sanitizeProps(value);
    }
  }

  return result;
}

interface Block {
  __component: string;
  [key: string]: any;
}

const props = defineProps({
  blocks: {
    type: Array as PropType<Block[]>,
    required: true,
  },
});

const { getComponent } = useStrapiUtils();

// Run once on client to print detailed debug info for each block
if (import.meta.client) {
  watchEffect(() => {
    console.log("[DynamicZone] Debug - watchEffect triggered");
    if (props.blocks && props.blocks.length) {
      console.log(
        `[DynamicZone] Received ${props.blocks.length} blocks:`,
        props.blocks
      );
      props.blocks.forEach((block, index) => {
        console.log(`[DynamicZone] Block ${index}:`, {
          __typename: block.__typename,
          __component: block.__component,
          id: block.id,
          heading: block.heading,
          hasSwiper: Array.isArray(block.swiper) && block.swiper.length > 0,
          swiperData: block.swiper || "No swiper data",
          fullBlock: block,
        });
      });
    } else {
      console.warn(
        "[DynamicZone] Received no blocks or invalid blocks:",
        props.blocks
      );
    }
  });
}

const components = computed(() => {
  if (!props.blocks || !Array.isArray(props.blocks)) {
    console.warn("DynamicZone received invalid blocks:", props.blocks);
    return [];
  }
  console.log("DynamicZone blocks:", props.blocks);
  return props.blocks.map((block) => {
    // Convert ComponentPagePageHero -> page.page-hero
    let componentName = "";

    if (block.__component) {
      componentName = block.__component;
    } else if (block.__typename) {
      // Only process __typename if it exists
      componentName = block.__typename
        .replace("Component", "")
        .replace(/([A-Z])/g, (match: string) => `-${match.toLowerCase()}`)
        .replace(/^-/, "")
        .replace(/^(\w+)-/, "$1.") // Convert first dash to dot
        .toLowerCase();
    } else {
      // Fallback for when neither property exists
      console.warn(
        "Block is missing both __component and __typename properties:",
        block
      );
      componentName = "missing-component";
    }

    console.log(
      `DynamicZone mapping: ${block.__typename || "unknown"} → ${componentName}`
    );
    const component = getComponent(componentName);

    console.log(
      `[DynamicZone] Component lookup result for '${componentName}':`,
      {
        originalBlock: {
          __typename: block.__typename,
          __component: block.__component,
        },
        derivedComponentName: componentName,
        componentFound: component !== null,
        componentType: typeof component,
      }
    );

    // Sanitize all props to prevent [object Object] URLs
    const sanitizedBlock = sanitizeProps(block);

    return {
      component,
      props: {
        // Use sanitized versions of all props
        content: sanitizedBlock,
        section: sanitizedBlock, // Pass the same block as section to satisfy requirements
        id: block.id || null,
        customId: block.customId || null,
        __typename: block.__typename || null,
      },
    };
  });
});
</script>
